import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import Button from '../../components/Button';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const STEPS = ['Set Week Day', 'Set Calendar', 'Salary Pay Day', 'OT Pay Day', 'Unpaid Leave Pay Day'] as const;

export default function PayDayGroupWizard() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [step, setStep] = useState<(typeof STEPS)[number]>('Set Week Day');
  const [group, setGroup] = useState<any>(null);
  const [weekdays, setWeekdays] = useState<any[]>([]);
  const [calendar, setCalendar] = useState<any[]>([]);
  const [holidayGroups, setHolidayGroups] = useState<any[]>([]);
  const [periods, setPeriods] = useState<Record<string, any[]>>({ SALARY: [], OT: [], UNPAID_LEAVE: [] });
  const [message, setMessage] = useState('');

  const load = () => {
    api.get(`/payroll/pay-day-groups/${id}`).then((res) => {
      setGroup(res.data.payDayGroup);
      setWeekdays(res.data.weekdays);
      setCalendar(res.data.calendar);
      const grouped: Record<string, any[]> = { SALARY: [], OT: [], UNPAID_LEAVE: [] };
      for (const p of res.data.periods) grouped[p.period_type].push(p);
      setPeriods(grouped);
    });
    api.get('/payroll/masters/holiday-groups').then((res) => setHolidayGroups(res.data.holidayGroups));
  };
  useEffect(load, [id]);

  const flash = (msg: string) => {
    setMessage(msg);
    setTimeout(() => setMessage(''), 2500);
  };

  const saveWeekdays = async () => {
    await api.put(`/payroll/pay-day-groups/${id}/weekdays`, {
      weekdays: weekdays.map((w) => ({ weekday: w.weekday, dayType: w.day_type })),
    });
    flash('Week day settings saved.');
  };

  const saveCalendar = async () => {
    await api.put(`/payroll/pay-day-groups/${id}/calendar`, {
      calendar: calendar.map((c) => ({ month: c.month, workingDays: c.working_days, holidayGroupId: c.holiday_group_id })),
    });
    flash('Calendar saved.');
  };

  const copyCalendarToAll = () => {
    if (!calendar.length) return;
    const first = calendar[0];
    setCalendar(calendar.map((c) => ({ ...c, working_days: first.working_days, holiday_group_id: first.holiday_group_id })));
  };

  const savePeriods = async (periodType: string) => {
    await api.put(`/payroll/pay-day-groups/${id}/periods`, {
      periodType,
      periods: periods[periodType].map((p) => ({
        month: p.month,
        segment: p.segment,
        cutoffFromDay: p.cutoff_from_day,
        cutoffToDay: p.cutoff_to_day,
        cutoffFromMonthOffset: p.cutoff_from_month_offset,
        cutoffToMonthOffset: p.cutoff_to_month_offset,
        payDateDay: p.pay_date_day,
      })),
    });
    flash(`${periodType} pay day saved.`);
  };

  const addMidSegment = (periodType: string) => {
    const endRows = periods[periodType].filter((p) => p.segment === 'END');
    const midRows = endRows.map((p) => ({ ...p, segment: 'MID', cutoff_from_day: 1, cutoff_to_day: 15, pay_date_day: 15 }));
    setPeriods({ ...periods, [periodType]: [...periods[periodType], ...midRows] });
  };

  const copyPeriodsToAll = (periodType: string) => {
    const rows = periods[periodType];
    if (!rows.length) return;
    const first = rows[0];
    setPeriods({
      ...periods,
      [periodType]: rows.map((p) => ({
        ...p,
        cutoff_from_day: first.cutoff_from_day,
        cutoff_to_day: first.cutoff_to_day,
        cutoff_from_month_offset: first.cutoff_from_month_offset,
        cutoff_to_month_offset: first.cutoff_to_month_offset,
        pay_date_day: first.pay_date_day,
      })),
    });
  };

  const updateWeekday = (weekday: number, dayType: string) =>
    setWeekdays(weekdays.map((w) => (w.weekday === weekday ? { ...w, day_type: dayType } : w)));

  const updateCalendarRow = (month: number, field: string, value: any) =>
    setCalendar(calendar.map((c) => (c.month === month ? { ...c, [field]: value } : c)));

  const updatePeriodRow = (periodType: string, month: number, segment: string, field: string, value: any) =>
    setPeriods({
      ...periods,
      [periodType]: periods[periodType].map((p) => (p.month === month && p.segment === segment ? { ...p, [field]: value } : p)),
    });

  if (!group) return <div className="p-6">Loading...</div>;

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold text-gray-800">
          Pay Day Group: {group.code} — {group.name} ({group.year})
        </h1>
        <Button variant="secondary" onClick={() => navigate('/payroll/pay-day-groups')}>
          Back to List
        </Button>
      </div>

      <div className="flex gap-2 mb-6 border-b overflow-x-auto">
        {STEPS.map((s) => (
          <button
            key={s}
            onClick={() => setStep(s)}
            className={`px-4 py-2 text-sm font-medium whitespace-nowrap ${step === s ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500'}`}
          >
            {s}
          </button>
        ))}
      </div>
      {message && <div className="mb-3 text-sm text-green-700 bg-green-50 px-3 py-2 rounded">{message}</div>}

      {step === 'Set Week Day' && (
        <div className="bg-white rounded-lg shadow p-4">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-600">
                <th className="px-3 py-2">Weekday</th>
                <th className="px-3 py-2">Day Type</th>
              </tr>
            </thead>
            <tbody>
              {weekdays.map((w) => (
                <tr key={w.weekday} className="border-t">
                  <td className="px-3 py-2">{WEEKDAYS[w.weekday]}</td>
                  <td className="px-3 py-2">
                    <select
                      className="px-2 py-1 border rounded"
                      value={w.day_type}
                      onChange={(e) => updateWeekday(w.weekday, e.target.value)}
                    >
                      <option value="FULL">Full</option>
                      <option value="HALF">Half</option>
                      <option value="REST">Rest</option>
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Button className="mt-4" onClick={saveWeekdays}>
            Save
          </Button>
        </div>
      )}

      {step === 'Set Calendar' && (
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex justify-end mb-2">
            <Button variant="secondary" onClick={copyCalendarToAll}>
              Copy Row 1 to All Rows
            </Button>
          </div>
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-600">
                <th className="px-3 py-2">Month</th>
                <th className="px-3 py-2">Working Days</th>
                <th className="px-3 py-2">Holiday Group</th>
              </tr>
            </thead>
            <tbody>
              {calendar.map((c) => (
                <tr key={c.month} className="border-t">
                  <td className="px-3 py-2">{MONTHS[c.month - 1]}</td>
                  <td className="px-3 py-2">
                    <input
                      type="number"
                      className="w-20 px-2 py-1 border rounded"
                      value={c.working_days || ''}
                      onChange={(e) => updateCalendarRow(c.month, 'working_days', e.target.value)}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <select
                      className="px-2 py-1 border rounded"
                      value={c.holiday_group_id || ''}
                      onChange={(e) => updateCalendarRow(c.month, 'holiday_group_id', e.target.value || null)}
                    >
                      <option value="">-- None --</option>
                      {holidayGroups.map((h) => (
                        <option key={h.id} value={h.id}>
                          {h.name}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Button className="mt-4" onClick={saveCalendar}>
            Save
          </Button>
        </div>
      )}

      {(['Salary Pay Day', 'OT Pay Day', 'Unpaid Leave Pay Day'] as const).includes(step as any) && (
        <PeriodStep
          periodType={step === 'Salary Pay Day' ? 'SALARY' : step === 'OT Pay Day' ? 'OT' : 'UNPAID_LEAVE'}
          rows={periods[step === 'Salary Pay Day' ? 'SALARY' : step === 'OT Pay Day' ? 'OT' : 'UNPAID_LEAVE']}
          onChange={updatePeriodRow}
          onCopy={copyPeriodsToAll}
          onSave={savePeriods}
          isBiMonthly={group.salary_pay_mode === 'BI_MONTHLY'}
          onAddMidSegment={addMidSegment}
        />
      )}
    </div>
  );
}

function PeriodStep({
  periodType,
  rows,
  onChange,
  onCopy,
  onSave,
  isBiMonthly,
  onAddMidSegment,
}: {
  periodType: string;
  rows: any[];
  onChange: (periodType: string, month: number, segment: string, field: string, value: any) => void;
  onCopy: (periodType: string) => void;
  onSave: (periodType: string) => void;
  isBiMonthly: boolean;
  onAddMidSegment?: (periodType: string) => void;
}) {
  const hasMid = rows.some((r) => r.segment === 'MID');

  return (
    <div className="bg-white rounded-lg shadow p-4">
      <div className="flex justify-end gap-2 mb-2">
        {isBiMonthly && !hasMid && onAddMidSegment && (
          <Button variant="secondary" onClick={() => onAddMidSegment(periodType)}>
            Add Mid-Month Segment
          </Button>
        )}
        <Button variant="secondary" onClick={() => onCopy(periodType)}>
          Copy Row 1 to All Rows
        </Button>
      </div>
      <table className="min-w-full text-sm">
        <thead>
          <tr className="text-left text-gray-600">
            <th className="px-3 py-2">Month</th>
            {isBiMonthly && <th className="px-3 py-2">Segment</th>}
            <th className="px-3 py-2">Cut-off From (day)</th>
            <th className="px-3 py-2">Cut-off To (day)</th>
            <th className="px-3 py-2">Pay Date (day)</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((p) => (
            <tr key={`${p.month}-${p.segment}`} className="border-t">
              <td className="px-3 py-2">{MONTHS[p.month - 1]}</td>
              {isBiMonthly && <td className="px-3 py-2">{p.segment}</td>}
              <td className="px-3 py-2">
                <input
                  type="number"
                  className="w-16 px-2 py-1 border rounded"
                  value={p.cutoff_from_day}
                  onChange={(e) => onChange(periodType, p.month, p.segment, 'cutoff_from_day', Number(e.target.value))}
                />
              </td>
              <td className="px-3 py-2">
                <input
                  type="number"
                  className="w-16 px-2 py-1 border rounded"
                  value={p.cutoff_to_day}
                  onChange={(e) => onChange(periodType, p.month, p.segment, 'cutoff_to_day', Number(e.target.value))}
                />
              </td>
              <td className="px-3 py-2">
                <input
                  type="number"
                  className="w-16 px-2 py-1 border rounded"
                  value={p.pay_date_day || ''}
                  onChange={(e) => onChange(periodType, p.month, p.segment, 'pay_date_day', Number(e.target.value))}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <Button className="mt-4" onClick={() => onSave(periodType)}>
        Save
      </Button>
    </div>
  );
}
